/**
 * @file: ThreadRow.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * One row in the inbox list. Renders:
 *  - leading circular icon (color from scope/category)
 *  - title + meta line (vehicle / customer / scope)
 *  - preview line (from latest message)
 *  - trailing time + unread badge
 */

import { TagBadge } from '@euler/app/components/task-card/components/TagBadge';
import { Typography } from '@euler/app/components/typography';
import { useInboxListTheme } from '@euler/app/flows/inbox/lib/listTheme';
import { Icon, IconType } from '@euler/components/Icomoon';
import { UnreadBadge } from '@euler/components/UnreadBadge';
import { Img } from '@euler/components/image/AdvancedImage';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { FeedThreadInboxItem } from '@euler/model/feed';
import { BellOff } from 'lucide-react-native';
import { FC, memo, useMemo } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import {
  descriptorForThread,
  feedThreadTier,
} from '../utils/feedThreadDescriptor';
import { relativeTime } from '../utils/relativeTime';
import { MediaStrip } from './MediaStrip';

type Props = {
  thread: FeedThreadInboxItem;
  active?: boolean;
  /** Edge-to-edge bottom hairline (marks the pinned/unpinned section
   * boundary); otherwise the line insets past the leading icon. */
  fullWidthDivider?: boolean;
  onPress?: () => void;
};

export const ThreadRow: FC<Props> = memo(
  ({ thread, active, fullWidthDivider, onPress }) => {
    const { rpx } = useResponsiveContext();
    const theme = useInboxListTheme();
    const desc = useMemo(() => descriptorForThread(thread), [thread]);
    const tier = useMemo(() => feedThreadTier(thread), [thread]);
    const unread = thread.unreadCount;
    const muted = thread.mutedAt != null;
    const time = relativeTime(thread.latestActivityAt);

    // Row + divider + text colors all flow from the inbox-list theme so
    // the desktop master-detail and the plain-white mobile surface stay
    // in sync. Tier selection is unchanged — only the palette swaps.
    const selected = !!active;
    const rowBg = selected
      ? theme.row.selected
      : tier === 'default-pinned'
      ? theme.row.defaultPinned
      : tier === 'other-pinned'
      ? theme.row.otherPinned
      : theme.row.unpinned;
    const dividerColor = fullWidthDivider
      ? theme.divider.section
      : tier === 'default-pinned'
      ? theme.divider.defaultPinned
      : tier === 'other-pinned'
      ? theme.divider.otherPinned
      : theme.divider.unpinned;
    const titleColor = selected ? theme.text.titleSelected : theme.text.title;
    const metaColor = selected ? theme.text.metaSelected : theme.text.meta;
    const timeColor = selected ? theme.text.timeSelected : theme.text.time;
    const previewColor = selected
      ? theme.text.previewSelected
      : desc.previewAccent ??
        (unread ? theme.text.previewUnread : theme.text.previewRead);
    const mutedIconColor = selected
      ? theme.text.mutedIconSelected
      : theme.text.mutedIcon;

    return (
      <TouchableOpacity
        onPress={onPress}
        activeOpacity={0.7}
        css={`
          position: relative;
          flex-direction: row;
          align-items: flex-start;
          background-color: ${rowBg};
          padding: ${rpx(14)}px ${rpx(16)}px;
        `}
      >
        <View
          css={`
            width: ${rpx(42)}px;
            height: ${rpx(42)}px;
            border-radius: ${rpx(12)}px;
            background-color: ${desc.brandLogo ? '#f3f3f5' : desc.accent};
            align-items: center;
            justify-content: center;
            margin-right: ${rpx(12)}px;
            overflow: hidden;
          `}
        >
          {desc.brandLogo ? (
            <Img
              uri={desc.brandLogo}
              resizeMode="contain"
              css={`
                width: ${rpx(30)}px;
                height: ${rpx(30)}px;
              `}
            />
          ) : (
            <Icon name={desc.icon as IconType} size={rpx(20)} color="#fff" />
          )}
        </View>

        <View style={{ flex: 1, minWidth: 0 }}>
          <View
            css={`
              flex-direction: row;
              align-items: center;
              justify-content: space-between;
            `}
          >
            <Typography.Text
              numberOfLines={1}
              size={rpx(15)}
              color={titleColor}
              medium
              style={{ flex: 1 }}
            >
              {desc.title}
            </Typography.Text>
            <Typography.Text size={rpx(11)} color={timeColor} ml={rpx(8)}>
              {time}
            </Typography.Text>
          </View>

          {desc.meta || desc.orderDateLabel ? (
            <Typography.Text
              numberOfLines={1}
              size={rpx(11)}
              color={metaColor}
              mt={rpx(2)}
            >
              {[desc.meta, desc.orderDateLabel].filter(Boolean).join(' · ')}
            </Typography.Text>
          ) : null}

          {desc.tags && desc.tags.length > 0 ? (
            <View
              css={`
                flex-direction: row;
                flex-wrap: wrap;
                margin-top: ${rpx(6)}px;
              `}
            >
              {desc.tags.slice(0, 4).map(tag => (
                <View
                  key={tag}
                  css={`
                    margin-bottom: ${rpx(2)}px;
                  `}
                >
                  <TagBadge label={tag} />
                </View>
              ))}
            </View>
          ) : null}

          <View
            css={`
              flex-direction: row;
              align-items: center;
              justify-content: space-between;
              margin-top: ${rpx(4)}px;
            `}
          >
            <View
              style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}
            >
              <Typography.Text
                numberOfLines={desc.previewWrap ? undefined : 2}
                size={rpx(13)}
                lineHeight={desc.previewWrap ? rpx(20) : undefined}
                color={previewColor}
                family={desc.previewAccent != null ? 'medium' : undefined}
                style={{ flex: 1 }}
              >
                {desc.preview || ' '}
              </Typography.Text>
              {desc.medias && desc.medias.length > 0 ? (
                <View style={{ marginLeft: rpx(8) }}>
                  <MediaStrip medias={desc.medias} size={28} max={3} />
                </View>
              ) : null}
            </View>
            <View
              css={`
                flex-direction: row;
                align-items: center;
                margin-left: ${rpx(8)}px;
              `}
            >
              {muted && (
                <View style={{ marginRight: rpx(6) }}>
                  <BellOff
                    color={mutedIconColor}
                    size={rpx(14)}
                    strokeWidth={2}
                  />
                </View>
              )}
              <UnreadBadge count={unread} />
            </View>
          </View>
        </View>
        {/* Bottom hairline lives INSIDE the row's bottom edge (WeChat-style):
          the last pinned row gets the edge-to-edge section divider; the
          selected row paints clean over its solid blue fill. */}
        {selected ? null : (
          <View
            style={{
              position: 'absolute',
              left: fullWidthDivider ? 0 : rpx(70),
              right: 0,
              bottom: 0,
              height: StyleSheet.hairlineWidth,
              backgroundColor: dividerColor,
            }}
          />
        )}
      </TouchableOpacity>
    );
  },
);
